import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Plot from "./PlotlyChart";
import { processBinDataWithRange } from "../../graphView/processBinData";
import {
  getColorForDate,
  MAXIMALLY_DISTINCT_COLOR_COUNT,
} from "../../graphView/colors";
import {
  plotlyAxisBase,
  plotlyAxisBaseCategoricalX,
  plotlyAxisTitle,
  plotlyAxisTitleFont,
  plotlyAxisTickFont,
  plotlyGridProps,
} from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  useGraphV2PlotHeight,
} from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import "./wrVsLengthPlotly.css";

const CARD_W = 300;
const CARD_H = 240;

/** Sliding date window within the solid-color palette budget. */
function sliceDateWindow(dateKeyList, maxDates, windowStart) {
  const keys = Array.isArray(dateKeyList) ? dateKeyList : [];
  const windowSize = Math.max(1, Number(maxDates) || MAXIMALLY_DISTINCT_COLOR_COUNT);
  const maxStart = Math.max(0, keys.length - windowSize);
  const start = Math.min(Math.max(0, Number(windowStart) || 0), maxStart);
  const dateKeys = keys.slice(start, start + windowSize);
  return {
    dateKeys,
    windowSize,
    windowStart: start,
    maxStart,
    totalDates: keys.length,
    needsPaging: keys.length > windowSize,
    olderCount: start,
    newerCount: Math.max(0, keys.length - (start + dateKeys.length)),
  };
}

/** Larger typography for client feedback (axis titles, ticks, legend). */
function byDateAxisTitle(text, isMobile) {
  const base = plotlyAxisTitle(text, isMobile);
  return {
    ...base,
    font: { ...plotlyAxisTitleFont(isMobile), size: isMobile ? 16 : 22 },
  };
}

function byDateTickFont(isMobile) {
  return { ...plotlyAxisTickFont(isMobile, "default"), size: isMobile ? 12 : 14 };
}

function byDateLegendFont(isMobile) {
  return { ...plotlyAxisTickFont(isMobile, "default"), size: isMobile ? 16 : 20 };
}

/**
 * Y-axis ticks for "Percentage of Population vs. Bin Size (by sampling date)" only.
 * Rule aligned with non-date population chart:
 * max < 12 => step 1, max < 24 => step 2, ... with fixed 12 tick values.
 */
function buildByDatePopulationPctYTicks(maxPct) {
  const maxPctN = Number.isFinite(Number(maxPct)) ? Math.max(0, Number(maxPct)) : 0;
  const TICK_COUNT = 12;
  const step = Math.max(1, Math.ceil(maxPctN / 12));

  const ticks = Array.from({ length: TICK_COUNT }, (_, i) =>
    Number((i * step).toFixed(1))
  );
  const rangeMax = Number((step * TICK_COUNT).toFixed(1));
  return { ticks, rangeMax };
}

export default function NumberFrequencyVsBinByDateChart({
  graphData,
  selectedUnit,
  isMobile,
  selectedBinSize,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);
  const [dateWindowStart, setDateWindowStart] = useState(null);

  const unitWord = selectedUnit.value === "English" ? "Inches" : "mm";

  const dateWindowMeta = useMemo(() => {
    const allDateKeys = [
      ...new Set(graphData.map((d) => d.sampleDateKey || "unknown")),
    ].sort((a, b) => a.localeCompare(b));
    const preferredStart =
      dateWindowStart == null
        ? Math.max(0, allDateKeys.length - MAXIMALLY_DISTINCT_COLOR_COUNT)
        : dateWindowStart;
    return {
      allDateKeys,
      ...sliceDateWindow(
        allDateKeys,
        MAXIMALLY_DISTINCT_COLOR_COUNT,
        preferredStart
      ),
    };
  }, [graphData, dateWindowStart]);

  const dateKeysSignature = useMemo(() => {
    const keys = [
      ...new Set(graphData.map((d) => d.sampleDateKey || "unknown")),
    ].sort((a, b) => a.localeCompare(b));
    return keys.join("|");
  }, [graphData]);

  useEffect(() => {
    setDateWindowStart(null);
  }, [dateKeysSignature, selectedUnit.value, selectedBinSize?.value]);

  const { labels, traces, yTicks, yRangeMax } = useMemo(() => {
    const { dateKeys: dateKeyList } = dateWindowMeta;
    const visibleData = graphData.filter((d) =>
      dateKeyList.includes(d.sampleDateKey || "unknown")
    );

    const lengths = visibleData.map((d) => d.length);
    const minL = lengths.length ? Math.min(...lengths) : 0;
    const maxL = lengths.length ? Math.max(...lengths) : 0;

    const binOpts = {
      unitValue: selectedUnit.value,
      binSizeValue: selectedBinSize?.value,
    };
    const masterBins = processBinDataWithRange(visibleData, binOpts, minL, maxL);
    const binLabels = masterBins.map((b) => b.binLabel);

    const t = dateKeyList.map((dk) => {
      const rows = visibleData.filter((d) => (d.sampleDateKey || "unknown") === dk);
      const total = rows.length || 1;
      const bins = processBinDataWithRange(rows, binOpts, minL, maxL);
      const y = bins.map((b) => ((Math.floor(b.number || 0) || 0) / total) * 100);
      const nVals = bins.map((b) => Math.floor(b.number || 0));
      const nStock = bins.map((b) => Math.floor(b.stocking || 0));
      const nSamp = bins.map((b, i) => Math.max(0, nVals[i] - nStock[i]));
      const pctSamp = nSamp.map((n) => (n / total) * 100);
      const pctStock = nStock.map((n) => (n / total) * 100);
      const label = rows[0]?.sampleDateLabel || dk;
      const barColor = getColorForDate(dk, dateKeyList);
      return {
        type: "bar",
        name: label,
        x: binLabels,
        y,
        marker: { color: barColor },
        customdata: binLabels.map((_, i) => [
          nVals[i],
          y[i],
          barColor,
          nSamp[i],
          pctSamp[i],
          nStock[i],
          pctStock[i],
        ]),
        hovertemplate: "<extra></extra>",
      };
    });

    const flatY = t.flatMap((tr) => (Array.isArray(tr.y) ? tr.y : []));
    const maxPct = flatY.reduce((m, v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > m ? n : m;
    }, 0);
    const { ticks: yTicks0, rangeMax } = buildByDatePopulationPctYTicks(maxPct);

    return {
      labels: binLabels,
      traces: t,
      yTicks: yTicks0,
      yRangeMax: rangeMax,
    };
  }, [graphData, selectedUnit.value, selectedBinSize?.value, dateWindowMeta]);

  const canShowOlder = dateWindowMeta.needsPaging && dateWindowMeta.windowStart > 0;
  const canShowNewer =
    dateWindowMeta.needsPaging &&
    dateWindowMeta.windowStart < dateWindowMeta.maxStart;

  const showOlderDates = useCallback(() => {
    setDateWindowStart((prev) => {
      const current = prev == null ? dateWindowMeta.windowStart : prev;
      return Math.max(0, current - dateWindowMeta.windowSize);
    });
  }, [dateWindowMeta.windowStart, dateWindowMeta.windowSize]);

  const showNewerDates = useCallback(() => {
    setDateWindowStart((prev) => {
      const current = prev == null ? dateWindowMeta.windowStart : prev;
      return Math.min(
        dateWindowMeta.maxStart,
        current + dateWindowMeta.windowSize
      );
    });
  }, [
    dateWindowMeta.windowStart,
    dateWindowMeta.windowSize,
    dateWindowMeta.maxStart,
  ]);

  const legendLayout = useMemo(
    () =>
      plotlyHorizontalLegendForTraces(traces, isMobile, {
        font: byDateLegendFont(isMobile),
        legendFontSize: isMobile ? 16 : 20,
      }),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 620,
    desktopDefault: 760,
    legendExtra: legendLayout.extraHeight || 0,
    fullscreenReserved: isMobile ? 270 : 235,
  });

  const onHover = useCallback((eventData) => {
    const pt = eventData?.points?.[0];
    const el = wrapRef.current;
    const evt = eventData?.event;
    if (!pt || el == null) {
      return;
    }
    const traceType = pt.data?.type || pt.fullData?.type;
    if (traceType !== "bar") {
      return;
    }
    const cd = pt.customdata;
    if (!Array.isArray(cd) || cd.length < 3) {
      return;
    }
    const count = Number(cd[0]);
    const pctVal = Number(cd[1]);
    const barColor = String(cd[2] ?? "#2563eb");
    const nSamp = cd.length >= 7 ? Number(cd[3]) : NaN;
    const pctSamp = cd.length >= 7 ? Number(cd[4]) : NaN;
    const nStock = cd.length >= 7 ? Number(cd[5]) : NaN;
    const pctStock = cd.length >= 7 ? Number(cd[6]) : NaN;
    if (!Number.isFinite(pctVal)) {
      return;
    }

    const sessionLabel = String(pt.data?.name || pt.fullData?.name || "");
    const binLabel = String(pt.x ?? "");

    let cx;
    let cy;
    if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
      cx = evt.clientX;
      cy = evt.clientY;
    } else if (pt.bbox) {
      cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
      cy = pt.bbox.y0;
    } else {
      return;
    }

    const rect = el.getBoundingClientRect();
    const pad = 12;
    let left = cx - rect.left + pad;
    let top = cy - rect.top + pad;
    if (left + CARD_W > rect.width - 8) {
      left = cx - rect.left - CARD_W - pad;
    }
    if (top + CARD_H > rect.height - 8) {
      top = cy - rect.top - CARD_H - pad;
    }
    left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
    top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

    setTip({
      left,
      top,
      sessionLabel,
      binLabel,
      count,
      pctVal,
      barColor,
      nSamp,
      pctSamp,
      nStock,
      pctStock,
    });
  }, []);

  const onUnhover = useCallback(() => setTip(null), []);

  const pageFrom = dateWindowMeta.windowStart + 1;
  const pageTo = dateWindowMeta.windowStart + dateWindowMeta.dateKeys.length;
  const pagingTitle = dateWindowMeta.needsPaging
    ? `Showing ${dateWindowMeta.dateKeys.length} of ${dateWindowMeta.totalDates} sampling dates so each series keeps a distinct color.`
    : undefined;
  const pagingStatus = dateWindowMeta.needsPaging
    ? `${pageFrom}–${pageTo} of ${dateWindowMeta.totalDates}`
    : null;

  const datePager = dateWindowMeta.needsPaging ? (
    <div
      className="d-flex align-items-center gap-2 flex-wrap justify-content-end"
      role="group"
      aria-label="Sampling date window"
      style={{ fontSize: isMobile ? 12 : 13 }}
    >
      <span
        className="text-muted"
        title={pagingTitle}
        style={{
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          maxWidth: isMobile ? "100%" : "220px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        Dates {pagingStatus}
        {dateWindowMeta.olderCount > 0 || dateWindowMeta.newerCount > 0
          ? ` · ${[
              dateWindowMeta.olderCount > 0
                ? `${dateWindowMeta.olderCount} older`
                : null,
              dateWindowMeta.newerCount > 0
                ? `${dateWindowMeta.newerCount} newer`
                : null,
            ]
              .filter(Boolean)
              .join(", ")}`
          : ""}
      </span>
      <div className="btn-group" role="group" aria-label="Page sampling dates">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={showOlderDates}
          disabled={!canShowOlder}
          title="Show previous (older) sampling dates"
          aria-label="Previous dates"
        >
          <i className="fa fa-chevron-left" aria-hidden />
          <span className="ms-1 d-none d-sm-inline">Previous</span>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={showNewerDates}
          disabled={!canShowNewer}
          title="Show next (newer) sampling dates"
          aria-label="Next dates"
        >
          <span className="me-1 d-none d-sm-inline">Next</span>
          <i className="fa fa-chevron-right" aria-hidden />
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={wrapRef}
      className="number-frequency-vs-bin-by-date-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <GraphV2FullscreenInlineRow endExtras={datePager} />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: isMobile ? 170 : 150, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          barmode: "group",
          bargap: 0.1,
          bargroupgap: 0.02,
          xaxis: {
            title: byDateAxisTitle(
              `Length Bin (${selectedUnit.value === "English" ? "Inches" : "mm"})`,
              isMobile
            ),
            tickangle: -90,
            categoryorder: "array",
            categoryarray: labels,
            ...plotlyAxisBaseCategoricalX(isMobile),
            /* Vertical gridlines at each bin (categorical X defaults to no grid). */
            ...plotlyGridProps(),
            tickfont: byDateTickFont(isMobile),
          },
          yaxis: {
            title: byDateAxisTitle("Percentage of Population", isMobile),
            rangemode: "tozero",
            tickmode: "array",
            tickvals: yTicks,
            range: [0, yRangeMax || 10],
            ticksuffix: "%",
            tickformat: ".1f",
            ...plotlyAxisBase(isMobile),
            tickfont: byDateTickFont(isMobile),
          },
          showlegend: true,
          legend: legendLayout.legend,
          hovermode: "closest",
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--nf-bin-date${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{ backgroundColor: tip.barColor }}
                aria-hidden
              />
              {tip.sessionLabel}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length bin:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.binLabel} {unitWord}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Number:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                {Number.isFinite(tip.count) ? String(Math.round(tip.count)) : "—"}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">% within session:</span>
              <span
                className="wr-length-hover-card__value"
                style={{
                  color: tip.barColor,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {tip.pctVal.toFixed(1)}%
              </span>
            </div>
            {Number.isFinite(tip.nStock) && tip.nStock > 0 && (
              <>
                <div className="wr-length-hover-card__row">
                  <span className="wr-length-hover-card__label">Sampling fish:</span>
                  <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                    {Math.round(tip.nSamp)} ({Number.isFinite(tip.pctSamp) ? tip.pctSamp.toFixed(1) : "—"}%)
                  </span>
                </div>
                <div className="wr-length-hover-card__row">
                  <span className="wr-length-hover-card__label">Stocked fish:</span>
                  <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                    {Math.round(tip.nStock)} ({Number.isFinite(tip.pctStock) ? tip.pctStock.toFixed(1) : "—"}%)
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
